/**
 * zCRM i18n: Atomic CRM's English messages with every object and field label taken from the
 * vocabulary dictionary. Each vocabulary mode is a locale, so switching mode is switching locale
 * (build spec, "Frontend-only architecture"): `en` is the vendor mode, `en-x-pm` the
 * property-manager mode. Both are valid language tags, so dates still format in English.
 *
 * Each mode also has a "field mapping" variant (`-x-map`) that writes the stored zInspector
 * field after every label, e.g. "Deal type (Task.EntranceInstruction)": a way to check the
 * mapping on live data, screen by screen.
 */
import { mergeTranslations } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";

import { englishCrmMessages } from "../commons/englishCrmMessages";
import {
  ACCOUNT_DETAILS,
  COMMERCIAL_AREA,
  COMMERCIAL_TERMS,
} from "./crmConfig";
import {
  contactFields,
  label,
  leaseFields,
  messagesFor,
  objects,
  propertyFields,
  taskFields,
  type Mode,
  type Section,
} from "./vocabulary";

export const VENDOR_LOCALE = "en";
export const PM_LOCALE = "en-x-pm";
export const VENDOR_MAPPING_LOCALE = "en-x-map";
export const PM_MAPPING_LOCALE = "en-x-pm-map";

/** Which vocabulary mode a locale uses, and whether it shows the stored field names. */
export const localeSettings = (
  locale: string | undefined,
): { mode: Mode; showMapping: boolean } => ({
  mode: locale?.includes("-pm") ? "pm" : "vendor",
  showMapping: !!locale?.endsWith("-map"),
});

/** "Label (Table.Field)" in a field-mapping locale, the plain label otherwise. */
export const withMapping = (
  text: string,
  storedField: string,
  showMapping: boolean,
): string => (showMapping ? `${text} (${storedField})` : text);

/** The zInspector table behind each vocabulary section of field labels. */
const SECTION_TABLES = new Map<Section, string>([
  [propertyFields, "Property"],
  [contactFields, "Contact"],
  [taskFields, "Task"],
  [leaseFields, "Lease"],
]);
const SECTION_TABLES_BY_NAME: Record<string, string> = {
  propertyFields: "Property",
  contactFields: "Contact",
  taskFields: "Task",
  leaseFields: "Lease",
};

const plural = (word: string): string => {
  if (word.endsWith("s")) return word;
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
};

/** `name` in polyglot's "singular |||| plural" form, as ra-core resource names expect. */
const resourceName = (word: string, caption = ""): string =>
  `${word}${caption} |||| ${plural(word)}${caption}`;

const emptyList = (word: string) => ({
  title: `No ${plural(word).toLowerCase()} found`,
  description: `It seems your ${word.toLowerCase()} list is empty.`,
});

export const zcrmMessages = (mode: Mode, showMapping = false) => {
  // A label hidden in this mode falls back to the given word, so screens never show a blank.
  const word = (section: Section, stored: string, fallback = stored): string =>
    label(section, stored, mode) ?? fallback;
  /** A field label, followed by its stored field in a mapping locale. */
  const t = (section: Section, stored: string): string =>
    withMapping(
      word(section, stored),
      `${SECTION_TABLES.get(section)}.${stored}`,
      showMapping,
    );
  const caption = (stored: string) => (showMapping ? ` (${stored})` : "");

  const account = word(objects, "property_account", "Property");
  const contact = word(objects, "contact_tenant");
  const deal = word(objects, "task_sales");
  const engagement = word(objects, "lease");
  const teamMember = word(objects, "user");

  return {
    resources: {
      companies: {
        name: resourceName(account, caption("Property")),
        forcedCaseName: account,
        fields: {
          name: t(propertyFields, "Name"),
          address: t(propertyFields, "Address"),
          city: t(propertyFields, "City"),
          state_abbr: t(propertyFields, "State"),
          zipcode: t(propertyFields, "Zip"),
          country: t(propertyFields, "Country"),
          market: t(propertyFields, "UserGroup"),
          accounting_id: t(propertyFields, "AccountingID"),
          last_activity: t(propertyFields, "LastActivity"),
          is_archived: t(propertyFields, "IsArchived"),
          account_details: withMapping(
            word(objects, "zdata_account_area"),
            'zData area "Account"',
            showMapping,
          ),
          // The "Account" zData details, labelled with their exact detail name.
          ...Object.fromEntries(
            ACCOUNT_DETAILS.map(({ key, detail }) => [
              key,
              withMapping(detail, `zData Account › ${detail}`, showMapping),
            ]),
          ),
          health: withMapping(
            "Health",
            "zData Account › Health segment",
            showMapping,
          ),
          status: withMapping("Status", "Lease.Status", showMapping),
          signed_on: withMapping("Signed", "Lease.LeaseStart", showMapping),
          agreement_state: t(leaseFields, "SigStatus"),
          main_contact_name: t(leaseFields, "PrimaryTenant"),
          account_manager_id: withMapping(
            "Account manager",
            `Task.ManagedBy, "Account journal" task`,
            showMapping,
          ),
          next_step: withMapping(
            "Next step",
            "Task.NextToDoName / Follow-up task",
            showMapping,
          ),
          last_touch: withMapping(
            "Last touch",
            "Task.LastModified",
            showMapping,
          ),
          open_deals: withMapping(
            `Open ${plural(deal).toLowerCase()}`,
            "Sales tasks",
            showMapping,
          ),
          open_tickets: withMapping(
            "Open tickets",
            "Support tasks",
            showMapping,
          ),
          nb_contacts: plural(contact),
          // The "Commercial" zData details (the client's terms), by their exact detail name.
          commercial: Object.fromEntries(
            COMMERCIAL_TERMS.map(({ key, detail }) => [
              key,
              withMapping(
                detail,
                `zData ${COMMERCIAL_AREA} › ${detail}`,
                showMapping,
              ),
            ]),
          ),
          doors: withMapping(
            "Doors (served / total)",
            "zData Account › Doors served, Serviceable doors",
            showMapping,
          ),
          open_work: withMapping(
            `${plural(deal)} · Tickets`,
            "open Sales / Support tasks",
            showMapping,
          ),
        },
        empty: emptyList(account),
        action: {
          new: `New ${account.toLowerCase()}`,
          create: `Create ${account.toLowerCase()}`,
        },
      },
      contacts: {
        name: resourceName(contact, caption("Contact, type Tenant")),
        forcedCaseName: contact,
        fields: {
          first_name: t(contactFields, "FirstName"),
          last_name: t(contactFields, "LastName"),
          title: t(contactFields, "TaxId"),
          role: t(contactFields, "TaxId"),
          company_id: withMapping(account, "Contact.Properties", showMapping),
          email: t(contactFields, "Email"),
          has_newsletter: t(contactFields, "Is1099Required"),
          is_primary: t(contactFields, "IsPrimary"),
          background: t(contactFields, "Comments"),
        },
        empty: emptyList(contact),
      },
      deals: {
        name: resourceName(deal, caption("Task, type Sales")),
        fields: {
          name: t(taskFields, "Description"),
          company_id: withMapping(account, "Task.Property", showMapping),
          stage: withMapping("Stage", "Task.Status", showMapping),
          expected_closing_date: withMapping(
            "Expected closing date",
            "Task.EndDate",
            showMapping,
          ),
          contact_ids: t(taskFields, "AssignedToContact"),
          category: t(taskFields, "EntranceInstruction"),
          product: t(taskFields, "Key"),
          amount: t(taskFields, "AuthorizedLimit"),
          description: t(taskFields, "WorkOrderInstructions"),
          loss_reason: t(taskFields, "KeyDesc"),
          sales_id: t(taskFields, "ManagedBy"),
          reference: t(taskFields, "ExternalReferenceNumber"),
          early_contact_name: t(taskFields, "TenantNames"),
          early_contact_phone: t(taskFields, "PhoneNumber"),
          early_contact_email: t(taskFields, "Email"),
          booked_at_booth: t(taskFields, "Pets"),
          created_at: t(taskFields, "DateCreated"),
          updated_at: t(taskFields, "LastModified"),
        },
        empty: {
          ...emptyList(deal),
          before_create: `before creating a ${deal.toLowerCase()}.`,
        },
      },
      leases: {
        name: resourceName(engagement, caption("Lease")),
        fields: {
          status: t(leaseFields, "Status"),
          type: t(leaseFields, "Type"),
          signature_status: t(leaseFields, "SigStatus"),
          start_date: t(leaseFields, "LeaseStart"),
          end_date: t(leaseFields, "LeaseEnd"),
          move_in: t(leaseFields, "MoveIn"),
          move_out: t(leaseFields, "MoveOut"),
          rent: t(leaseFields, "Rent"),
          comments: t(leaseFields, "Comments"),
          main_contact_name: t(leaseFields, "PrimaryTenant"),
          participant_ids: t(leaseFields, "Tenants"),
        },
      },
      sales: {
        name: resourceName(teamMember, caption("User")),
      },
      leads: {
        name: resourceName("Lead", caption("Task, type Prospect")),
      },
      tickets: {
        name: resourceName("Ticket", caption("Task, type Support")),
        fields: {
          subject: t(taskFields, "Description"),
          status: withMapping("Status", "Task.Status", showMapping),
          reference: withMapping(
            "Reference",
            "Task.ReferenceNumber",
            showMapping,
          ),
          updated_at: t(taskFields, "LastModified"),
        },
      },
    },
    crm: {
      language: "Vocabulary",
    },
    ...vocabularyMessages(mode, showMapping),
  };
};

/** The raw vocabulary under `zi.*`, with stored field names on field labels in a mapping locale. */
const vocabularyMessages = (mode: Mode, showMapping: boolean) => {
  const { zi } = messagesFor(mode);
  if (!showMapping) return { zi };
  return {
    zi: Object.fromEntries(
      Object.entries(zi).map(([sectionName, labels]) => {
        const table = SECTION_TABLES_BY_NAME[sectionName];
        if (!table) return [sectionName, labels];
        return [
          sectionName,
          Object.fromEntries(
            Object.entries(labels).map(([stored, text]) => [
              stored,
              withMapping(text, `${table}.${stored}`, true),
            ]),
          ),
        ];
      }),
    ),
  };
};

const catalog = (mode: Mode, showMapping: boolean) =>
  mergeTranslations(
    englishMessages,
    englishCrmMessages,
    zcrmMessages(mode, showMapping),
  );

const catalogs: Record<string, ReturnType<typeof catalog>> = {
  [VENDOR_LOCALE]: catalog("vendor", false),
  [PM_LOCALE]: catalog("pm", false),
  [VENDOR_MAPPING_LOCALE]: catalog("vendor", true),
  [PM_MAPPING_LOCALE]: catalog("pm", true),
};

export const zcrmI18nProvider = polyglotI18nProvider(
  (locale) => catalogs[locale] ?? catalogs[VENDOR_LOCALE],
  VENDOR_LOCALE,
  [
    { locale: VENDOR_LOCALE, name: "Vendor" },
    { locale: PM_LOCALE, name: "Property manager" },
    { locale: VENDOR_MAPPING_LOCALE, name: "Vendor + field mapping" },
    { locale: PM_MAPPING_LOCALE, name: "Property manager + field mapping" },
  ],
  { allowMissing: true },
);
